// src/pages/container_rental/components/timeline/TimelineScale.ts
//
// 타임라인 스케일 — 순수 함수만 모아 둔 모듈(React 의존 없음, 테스트 대상).
//
// 설계 원칙
//  1) 모든 날짜는 KST(+09:00) 자정으로 정규화한 뒤 "origin 으로부터 며칠"이라는
//     정수 인덱스로 환산한다. 픽셀은 그 정수에 pxPerDay 를 곱해서만 만든다.
//     → 눈금을 순차적으로 더해 나가며 폭을 누적하지 않으므로 부동소수 오차가
//       뒤로 밀려 쌓이지 않는다.
//  2) KST 는 DST 가 없으므로 ms 차이가 항상 86,400,000 의 정수배다.
//     따라서 Math.round(Δms / MS_PER_DAY) 는 정확하다.

import type { Dayjs } from "dayjs";
import { toKst } from "../../../../utils/date.ts";

export const MS_PER_DAY = 86_400_000;

/* ------------------------------------------------------------------ */
/* 줌                                                                  */
/* ------------------------------------------------------------------ */

export type ZoomLevel = "month" | "quarter" | "year";

/** 배율 낮은 순(넓게 봄) → 높은 순(자세히 봄). [−]/[+] 버튼이 이 순서로 이동. */
export const ZOOM_LEVELS: ZoomLevel[] = ["year", "quarter", "month"];

/** 확정 상수 — 월 24px/일, 분기 8px/일, 년 2.2px/일. */
export const PX_PER_DAY: Record<ZoomLevel, number> = {
  month: 24,
  quarter: 8,
  year: 2.2,
};

export const pxPerDay = (zoom: ZoomLevel): number =>
  PX_PER_DAY[zoom] ?? PX_PER_DAY.quarter;

/** 더 자세히(월 방향). 끝이면 그대로. */
export function zoomIn(zoom: ZoomLevel): ZoomLevel {
  const i = ZOOM_LEVELS.indexOf(zoom);
  if (i < 0) return "quarter";
  return ZOOM_LEVELS[Math.min(i + 1, ZOOM_LEVELS.length - 1)];
}

/** 더 넓게(년 방향). 끝이면 그대로. */
export function zoomOut(zoom: ZoomLevel): ZoomLevel {
  const i = ZOOM_LEVELS.indexOf(zoom);
  if (i < 0) return "quarter";
  return ZOOM_LEVELS[Math.max(i - 1, 0)];
}

/* ------------------------------------------------------------------ */
/* 날짜 ↔ 픽셀                                                         */
/* ------------------------------------------------------------------ */

/** KST 자정으로 내린 dayjs. */
export const day0 = (d: Dayjs): Dayjs => toKst(d).startOf("day");

/** origin 자정 → date 자정 사이의 정수 일수(과거면 음수). */
export function dayDiff(origin: Dayjs, date: Dayjs): number {
  return Math.round((day0(date).valueOf() - day0(origin).valueOf()) / MS_PER_DAY);
}

/** origin 을 x=0 으로 두었을 때 date 가 놓이는 x 좌표(px). */
export function dateToX(origin: Dayjs, date: Dayjs, ppd: number): number {
  return dayDiff(origin, date) * ppd;
}

/**
 * x 좌표가 가리키는 "날(day)". x 가 하루 칸 안 어디에 있든 그 날의 자정을 준다.
 * ppd 가 2.2 같은 무리한 값이어도 되돌아오도록 미세 epsilon 을 더한다.
 */
export function xToDate(origin: Dayjs, x: number, ppd: number): Dayjs {
  const safe = ppd > 0 ? ppd : 1;
  const idx = Math.floor(x / safe + 1e-6);
  return day0(origin).add(idx, "day");
}

/** [from, to] 를 모두 담는 트랙 폭(px). to 가 속한 날 하루를 포함한다. */
export function trackWidth(from: Dayjs, to: Dayjs, ppd: number): number {
  return Math.max(dayDiff(from, to) + 1, 1) * ppd;
}

/** d 를 [from, to] 안으로 클램프. */
export function clampDate(d: Dayjs, from: Dayjs, to: Dayjs): Dayjs {
  if (d.valueOf() < from.valueOf()) return from;
  if (d.valueOf() > to.valueOf()) return to;
  return d;
}

/* ------------------------------------------------------------------ */
/* 도메인(보이는 기간) 계산                                            */
/* ------------------------------------------------------------------ */

export interface DateRange {
  from: Dayjs;
  to: Dayjs;
}

export interface Bound {
  start: Dayjs;
  end: Dayjs;
}

/** 사용자 명시 요구 — 초기 뷰는 항상 오늘−3개월 ~ 오늘+9개월. */
export function defaultViewRange(today: Dayjs): DateRange {
  const t = day0(today);
  return {
    from: t.subtract(3, "month").startOf("month"),
    to: t.add(9, "month").endOf("month").startOf("day"),
  };
}

/** 요청 창을 도메인으로 쓸 때 앞뒤로 두는 여유(개월). */
export const WINDOW_PAD_MONTHS = 1;

const isUsable = (d: Dayjs | null | undefined): d is Dayjs =>
  Boolean(d && d.isValid());

/**
 * 실제로 그릴 도메인.
 *
 * 두 가지 모드가 있다.
 *
 *  1) 요청 기간이 "둘 다" 지정된 경우(서버 필터가 실제로 걸리는 경우와 동일 조건)
 *     → 도메인을 그 창 ±1개월로 **제한**한다. 행의 바가 창 밖으로 뻗어 있어도
 *       도메인을 넓히지 않는다. 아주 오래된/먼 미래 계약 한 건 때문에 도메인이
 *       수년으로 벌어지고(월 줌 5년이면 트랙 폭 약 43,800px) 프리셋 3개월을
 *       골라도 차트가 줄지 않던 문제를 여기서 끊는다.
 *       창 밖으로 나간 바는 RentalBar 가 clampDate 로 잘라 그리고 잘림 표식을
 *       붙인다. 오늘이 창 밖일 수 있으므로 호출부는 오늘선을 조건부로 그린다.
 *
 *  2) 그 외(전체 보기 / 한쪽만 입력한 중간 상태)
 *     → 기본 뷰(오늘 기준) ∪ 요청값 ∪ 모든 바 범위 를 합집합한다.
 *       오늘선이 항상 도메인 안에 있고 어떤 바도 잘리지 않는다.
 *
 * 두 모드 모두 결과는 월 경계로 스냅한다.
 */
export function fitRange(opts: {
  today: Dayjs;
  bounds?: Bound[];
  requestedFrom?: Dayjs | null;
  requestedTo?: Dayjs | null;
}): DateRange {
  const rf = isUsable(opts.requestedFrom) ? day0(opts.requestedFrom) : null;
  const rt = isUsable(opts.requestedTo) ? day0(opts.requestedTo) : null;

  if (rf && rt && rf.valueOf() <= rt.valueOf()) {
    return {
      from: rf.subtract(WINDOW_PAD_MONTHS, "month").startOf("month"),
      to: rt.add(WINDOW_PAD_MONTHS, "month").endOf("month").startOf("day"),
    };
  }

  const base = defaultViewRange(opts.today);

  let from = base.from;
  let to = base.to;

  const lo = (d: Dayjs | null | undefined) => {
    if (d && d.isValid() && d.valueOf() < from.valueOf()) from = day0(d);
  };
  const hi = (d: Dayjs | null | undefined) => {
    if (d && d.isValid() && d.valueOf() > to.valueOf()) to = day0(d);
  };

  lo(opts.requestedFrom);
  hi(opts.requestedTo);

  (opts.bounds ?? []).forEach((b) => {
    lo(b.start);
    hi(b.end);
    // start > end 인 비정상 데이터도 양쪽 다 넣어 두면 안전하다.
    lo(b.end);
    hi(b.start);
  });

  return {
    from: from.startOf("month"),
    to: to.endOf("month").startOf("day"),
  };
}

/* ------------------------------------------------------------------ */
/* 눈금                                                                */
/* ------------------------------------------------------------------ */

export type TickKind = "month" | "week" | "day";

export interface Tick {
  key: string;
  kind: TickKind;
  /** 눈금 구간 시작(KST 자정) */
  start: Dayjs;
  /** 눈금 구간 끝(포함, KST 자정) */
  end: Dayjs;
  /** origin 기준 x(px) */
  x: number;
  /** 눈금 폭(px) */
  width: number;
  label: string;
  /** 월 시작 등 상위 경계 — 굵은 구분선 */
  major: boolean;
}

/** 그 주의 월요일(KST). dayjs locale 에 의존하지 않도록 직접 계산. */
export function startOfWeekMon(d: Dayjs): Dayjs {
  const base = day0(d);
  const dow = (base.day() + 6) % 7; // Mon=0 … Sun=6
  return base.subtract(dow, "day");
}

/** [from, to] 를 덮는 월 눈금. */
export function monthTicks(
  origin: Dayjs,
  from: Dayjs,
  to: Dayjs,
  ppd: number
): Tick[] {
  const out: Tick[] = [];
  const last = day0(to);
  let cursor = day0(from).startOf("month");

  // 무한 루프 방지 상한 (약 100년)
  for (let guard = 0; guard < 1300; guard += 1) {
    if (cursor.valueOf() > last.valueOf()) break;
    const next = cursor.add(1, "month");
    const segStart = cursor.valueOf() < day0(from).valueOf() ? day0(from) : cursor;
    const segEnd = next.subtract(1, "day");
    const clampedEnd = segEnd.valueOf() > last.valueOf() ? last : segEnd;

    out.push({
      key: `m-${cursor.format("YYYY-MM")}`,
      kind: "month",
      start: segStart,
      end: clampedEnd,
      x: dateToX(origin, segStart, ppd),
      width: Math.max(dayDiff(segStart, clampedEnd) + 1, 1) * ppd,
      label: cursor.format("YY.MM"),
      major: cursor.month() === 0,
    });
    cursor = next;
  }
  return out;
}

/** [from, to] 를 덮는 주 눈금(월요일 시작). */
export function weekTicks(
  origin: Dayjs,
  from: Dayjs,
  to: Dayjs,
  ppd: number
): Tick[] {
  const out: Tick[] = [];
  const first = day0(from);
  const last = day0(to);
  let cursor = startOfWeekMon(first);

  for (let guard = 0; guard < 6000; guard += 1) {
    if (cursor.valueOf() > last.valueOf()) break;
    const next = cursor.add(7, "day");
    const segStart = cursor.valueOf() < first.valueOf() ? first : cursor;
    const segEnd = next.subtract(1, "day");
    const clampedEnd = segEnd.valueOf() > last.valueOf() ? last : segEnd;

    out.push({
      key: `w-${cursor.format("YYYY-MM-DD")}`,
      kind: "week",
      start: segStart,
      end: clampedEnd,
      x: dateToX(origin, segStart, ppd),
      width: Math.max(dayDiff(segStart, clampedEnd) + 1, 1) * ppd,
      label: cursor.format("M/D"),
      major: cursor.date() <= 7,
    });
    cursor = next;
  }
  return out;
}

/** [from, to] 의 일 눈금. */
export function dayTicks(
  origin: Dayjs,
  from: Dayjs,
  to: Dayjs,
  ppd: number
): Tick[] {
  const out: Tick[] = [];
  const first = day0(from);
  const total = dayDiff(first, day0(to));

  for (let i = 0; i <= total && i < 40_000; i += 1) {
    const d = first.add(i, "day");
    const dow = d.day();
    out.push({
      key: `d-${d.format("YYYY-MM-DD")}`,
      kind: "day",
      start: d,
      end: d,
      x: (dayDiff(origin, first) + i) * ppd,
      width: ppd,
      label: d.format("D"),
      major: dow === 1, // 월요일
    });
  }
  return out;
}

export interface TickRows {
  top: Tick[];
  bottom: Tick[];
  bottomKind: TickKind;
  /** 하단 눈금 라벨을 그릴 만한 폭인지 */
  bottomLabelled: boolean;
}

/**
 * 줌별 2단 눈금.
 *  - month  : 상단 월 / 하단 일
 *  - quarter: 상단 월 / 하단 주
 *  - year   : 상단 월 / 하단 주(폭이 좁아 라벨은 생략)
 */
export function ticksForZoom(
  zoom: ZoomLevel,
  origin: Dayjs,
  from: Dayjs,
  to: Dayjs
): TickRows {
  const ppd = pxPerDay(zoom);
  const top = monthTicks(origin, from, to, ppd);

  if (zoom === "month") {
    const bottom = dayTicks(origin, from, to, ppd);
    return { top, bottom, bottomKind: "day", bottomLabelled: ppd >= 16 };
  }

  const bottom = weekTicks(origin, from, to, ppd);
  return {
    top,
    bottom,
    bottomKind: "week",
    bottomLabelled: ppd * 7 >= 34,
  };
}
